import { useMemo, useState } from 'react';
import {
  describeRouletteBet,
  type RouletteBetId,
  type RouletteBetNum,
  type RoulettePocket,
  type RouletteRules,
} from '@tv-casino/shared';
import type { PublicRouletteBet, RouletteBetSpec } from '@tv-casino/shared';
import { RouletteTable } from './RouletteTable.js';
import { FloatAction, useOffscreenWatch } from './FloatAction.js';
import type { RouletteSpotKey, RouletteStackMap } from '@tv-casino/viewmodels/components/rouletteFelt.js';
import {
  rouletteSettledLabel,
  spreadsThrough,
} from '@tv-casino/viewmodels/components/rouletteFelt.js';
import { chipTray } from '@tv-casino/viewmodels/components/chipStack.js';
import styles from './RouletteBet.module.css';

interface RouletteBetProps {
  rules: RouletteRules;
  /** Chips free to stake, outside anything already on the layout. */
  chips: number;
  /** Every stack on the layout, this seat's included — the shared felt. */
  stacks?: RouletteStackMap;
  /** The landed pocket, for the dolly on the winning number. */
  pocket?: RoulettePocket | null;
  /** Spots the spin paid, for the winner's flash. */
  marks?: Partial<Record<RouletteSpotKey, 'win'>>;
  /** This seat's placed stacks, newest last. */
  bets: PublicRouletteBet[];
  /** The previous spin's spread, for the rebet button. Empty before a spin. */
  lastBets?: RouletteBetSpec[];
  /** Total currently staked on the layout. */
  staked: number;
  /** True once the seat locked in with Done. */
  done: boolean;
  /**
   * False outside the betting window: the felt stays up with everyone's
   * stacks, but the rail will not drop or pull.
   */
  canDrop?: boolean;
  busy: boolean;
  onPlace: (betId: RouletteBetId, num: RouletteBetNum | undefined, amount: number) => void;
  /** Take one stack back, or the whole layout when no stack is named. */
  onClear: (stack?: { id: RouletteBetId; num?: RouletteBetNum }) => void;
  /** Put the previous spin's whole spread back down. */
  onRebet?: () => void;
  onDone: () => void;
}

const chipTone = (amount: number): string | undefined =>
  amount >= 500
    ? styles.tone500
    : amount >= 100
      ? styles.tone100
      : amount >= 25
        ? styles.tone25
        : styles.tone5;

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

/**
 * The phone's roulette layout, bet the way craps is: pick a chip out of the
 * rail, then tap a spot to drop it — every tap is a bet, exactly like sliding
 * a chip across the felt. Tap your own chip to take that stack back, sweep
 * for everything, and Done locks the seat in.
 *
 * The spreads keep one extra beat, because a finger cannot hit the line
 * between two cells: tap a number and the shapes through it are offered as
 * their own row, each one dropping the armed chip on a tap.
 */
export function RouletteBet({
  rules,
  chips,
  stacks = {},
  pocket = null,
  marks = {},
  bets,
  lastBets = [],
  staked,
  done,
  canDrop = true,
  busy,
  onPlace,
  onClear,
  onRebet,
  onDone,
}: RouletteBetProps) {
  const tray = useMemo(() => chipTray(rules.minBet), [rules.minBet]);
  const [denom, setDenom] = useState(tray[0]!);
  // The number cell last tapped. It opens the spread row and stays open while
  // the player compares the shapes through it.
  const [focus, setFocus] = useState<number | null>(null);

  /**
   * Reviewing the stacks list scrolls the lock-in below the fold while the
   * betting window runs, so the commit floats over the screen like craps'
   * roll. A seat with nothing down has nothing to lock in.
   */
  const doneWatch = useOffscreenWatch<HTMLButtonElement>(canDrop && !done && staked > 0);

  const spreads = useMemo(() => (focus === null ? [] : spreadsThrough(focus)), [focus]);

  /**
   * What a tap would put down right now: the armed chip, or nothing. Busy is
   * deliberately absent — the server arbitrates each stack, and a quick
   * second tap should not be eaten by the first tap's ack.
   */
  const takes = (betId: RouletteBetId): boolean =>
    canDrop && !done && denom >= rules.minBet && denom <= rules.maxBet && denom <= chips;

  const drop = (betId: RouletteBetId, num?: RouletteBetNum) => {
    if (!takes(betId)) return;
    onPlace(betId, num, denom);
  };

  const pullable = (): boolean => canDrop && !busy && !done;

  const rebetTotal = lastBets.reduce((sum, b) => sum + b.amount, 0);
  const rebettable =
    onRebet !== undefined && canDrop && !done && lastBets.length > 0 && rebetTotal <= chips;

  return (
    <section className={styles.wrap}>
      <p className={styles.heading}>
        {done
          ? 'Roulette — locked in'
          : canDrop
            ? 'Roulette — pick a chip, tap the felt'
            : 'Roulette — chips on the felt'}
      </p>

      <RouletteTable
        wheel={rules.wheel}
        stacks={stacks}
        pocket={pocket}
        marks={marks}
        canDrop={takes}
        onDrop={(betId, num) => {
          if (betId === 'straight' && typeof num === 'number') setFocus(num);
          else setFocus(null);
          drop(betId, num);
        }}
        onPull={(betId, num) => onClear({ id: betId, num })}
        canPull={pullable}
      />

      <div className={styles.rail} role="group" aria-label="Chip rail">
        {tray.map((chip) => (
          <button
            key={chip}
            type="button"
            className={cx(
              styles.railChip,
              chipTone(chip),
              denom === chip && styles.railChipOn,
              chip > chips && styles.railChipOff,
            )}
            aria-pressed={denom === chip}
            aria-label={`${chip} chip`}
            onClick={() => setDenom(chip)}
          >
            {chip}
          </button>
        ))}
        <span className={styles.railHint}>
          {done
            ? 'Locked in'
            : !canDrop
              ? 'Waiting on the spin'
              : busy
                ? 'One moment…'
                : takes('red')
                  ? 'Tap a spot to drop'
                  : 'Out of chips'}
        </span>
      </div>

      {spreads.length > 0 && (
        <div className={styles.spreads} role="group" aria-label={`Spreads through ${focus}`}>
          {spreads.map(({ id, anchor, label, odds }) => (
            <button
              key={`${id}-${anchor}`}
              type="button"
              className={styles.spread}
              disabled={done}
              onClick={() => drop(id, anchor)}
            >
              <span className={styles.spreadLabel}>{label}</span>
              <span className={styles.spreadOdds}>{odds}</span>
            </button>
          ))}
        </div>
      )}

      <div className={styles.actions}>
        {onRebet !== undefined && (
          <button
            type="button"
            className={styles.sweep}
            disabled={busy || !rebettable}
            onClick={onRebet}
            aria-label={`Rebet the previous spread, ${rebetTotal} chips`}
          >
            Rebet last ({rebetTotal})
          </button>
        )}
        <button
          type="button"
          className={styles.sweep}
          disabled={busy || !canDrop || done || staked === 0}
          onClick={() => onClear()}
        >
          Take back all ({staked})
        </button>
        <button
          ref={doneWatch.ref}
          type="button"
          className={done ? styles.doneOn : styles.done}
          disabled={busy || !canDrop || staked === 0 || done}
          onClick={onDone}
        >
          {done ? 'Locked in' : 'Done — lock in'}
        </button>
      </div>

      <FloatAction
        show={canDrop && !done && staked > 0 && doneWatch.offscreen}
        buttons={[{ label: 'Done — lock in', disabled: busy, onClick: onDone }]}
      />

      {bets.length > 0 && (
        <ul className={styles.stackList} aria-label="Your stacks">
          {bets.map((b, i) => (
            <li key={`${b.id}-${b.num ?? 'x'}-${i}`} className={styles.stackRow}>
              <span className={styles.stackLabel}>
                {describeRouletteBet(b.id, b.num)}
                {' · '}
                {b.amount}
                {b.payout != null && ` → ${rouletteSettledLabel(b.amount, b.payout)}`}
              </span>
              <button
                type="button"
                className={styles.stackClear}
                disabled={busy || done}
                onClick={() => onClear({ id: b.id, num: b.num })}
                aria-label={`Take back ${b.amount} on ${describeRouletteBet(b.id, b.num)}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {rules.laPartage && (
        <p className={styles.hint}>La partage: an even-money bet on a zero wins half back.</p>
      )}
      {canDrop && !done && (
        <p className={styles.hint}>
          Pick a chip, then tap a spot to drop it. Tap a number for the spreads through it; tap
          your own chip to take it back.
        </p>
      )}
    </section>
  );
}
